import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { formatUZS } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Calendar, Clock, Activity, CheckCircle2, AlertTriangle, XCircle, User, Package, FileText, MapPin, Hash, CreditCard, AlertCircle, Printer } from 'lucide-react';
import { generateContractPDF } from '@/lib/contract-pdf';
import ConsistencyBadge from '@/components/murabaha/ConsistencyBadge';
import { checkMurabahaConsistency } from '@/lib/murabaha-consistency';
import { AdminInvoicePanel } from '@/components/invoices/InvoiceCard';
import BankRegistrationPanel from '@/components/murabaha/BankRegistrationPanel';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { intlLocale } from './adminLocale';

const contractStatusLabels = (): Record<string, string> => ({
  draft: i18n.t('adm_contractDetail.cs_draft'), sent: i18n.t('adm_contractDetail.cs_sent'), signed: i18n.t('adm_contractDetail.cs_signed'), cancelled: i18n.t('adm_contractDetail.cs_cancelled'),
});
const contractStatusColors: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground', sent: 'bg-info/10 text-info',
  signed: 'bg-success/10 text-success', cancelled: 'bg-destructive/10 text-destructive',
};
const scheduleStatusLabels = (): Record<string, string> => ({
  due: i18n.t('adm_contractDetail.ss_due'), paid: i18n.t('adm_contractDetail.ss_paid'), overdue: i18n.t('adm_contractDetail.ss_overdue'), cancelled: i18n.t('adm_contractDetail.ss_cancelled'),
});
const scheduleStatusColors: Record<string, string> = {
  due: 'bg-warning/10 text-warning', paid: 'bg-success/10 text-success',
  overdue: 'bg-destructive/10 text-destructive', cancelled: 'bg-muted text-muted-foreground',
};
const orderStatusLabels = (): Record<string, string> => ({
  draft: i18n.t('adm_contractDetail.os_draft'), confirmed: i18n.t('adm_contractDetail.os_confirmed'), cancelled: i18n.t('adm_contractDetail.os_cancelled'),
});

const eventTypeLabels = (): Record<string, string> => ({
  created: i18n.t('adm_contractDetail.ev_created'), sent: i18n.t('adm_contractDetail.ev_sent'), signed: i18n.t('adm_contractDetail.ev_signed'),
  payment_received: i18n.t('adm_contractDetail.ev_payment_received'), overdue_notice: i18n.t('adm_contractDetail.ev_overdue_notice'),
  cancelled: i18n.t('adm_contractDetail.ev_cancelled'), status_change: i18n.t('adm_contractDetail.ev_status_change'),
});
const statusLabels = (): Record<string, string> => ({
  Draft: i18n.t('adm_contracts.st_draft'), PendingCustomerConfirmation: i18n.t('adm_contracts.st_pending'), Approved: i18n.t('adm_contracts.st_approved'),
  Rejected: i18n.t('adm_contracts.st_rejected'), Completed: i18n.t('adm_contracts.st_completed'), Cancelled: i18n.t('adm_contracts.st_cancelled'),
});
const statusColors: Record<string, string> = {
  Draft: 'bg-muted text-muted-foreground',
  PendingCustomerConfirmation: 'bg-warning/10 text-warning',
  Approved: 'bg-info/10 text-info',
  Rejected: 'bg-destructive/10 text-destructive',
  Completed: 'bg-success/10 text-success',
  Cancelled: 'bg-muted text-muted-foreground',
};
const paymentStatusLabels = (): Record<string, string> => ({
  none: i18n.t('adm_contractDetail.ps_none'), in_progress: i18n.t('adm_contracts.pay_inProgress'), cancelled: i18n.t('adm_contractDetail.ps_cancelled'), paid: i18n.t('adm_contracts.pay_paid'),
});
const paymentStatusColors: Record<string, string> = {
  none: 'bg-muted text-muted-foreground',
  in_progress: 'bg-warning/10 text-warning',
  cancelled: 'bg-destructive/10 text-destructive',
  paid: 'bg-success/10 text-success',
};

function StatusIcon({ status }: { status: number | null }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  if (status >= 200 && status < 300) return <CheckCircle2 size={14} className="text-success" />;
  if (status >= 400 && status < 500) return <AlertTriangle size={14} className="text-warning" />;
  return <XCircle size={14} className="text-destructive" />;
}

function InfoRow({ icon: Icon, label, value }: { icon?: React.ComponentType<{ size?: number; className?: string }>; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-2">
      {Icon && <Icon size={15} className="text-muted-foreground mt-0.5 shrink-0" />}
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm text-foreground break-all">{value || '—'}</p>
      </div>
    </div>
  );
}

export default function AdminContractDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const contractId = Number(id);
  const navigate = useNavigate();

  const { data: contractData, isLoading: loading } = useQuery({
    queryKey: [`/admin/contracts/{contract}`, { params: { path: { contract: contractId } } }],
  });
  const contract = contractData?.data;
  const mCase = contract?.murabaha_case;

  const { data: merchantData } = useQuery({
    queryKey: [`/admin/merchants/{merchant}`, { params: { path: { merchant: mCase?.merchant_id } } }],
    enabled: !!mCase?.merchant_id,
  });
  const merchant = merchantData?.data;

  const { data: customerData } = useQuery({
    queryKey: [`/customers/{customer}`, { params: { path: { customer: mCase?.customer_id } } }],
    enabled: !!mCase?.customer_id,
  });
  const customer = customerData?.data;

  const { data: branchData } = useQuery({
    queryKey: [`/branches/{branch}`, { params: { path: { branch: mCase?.branch_id } } }],
    enabled: !!mCase?.branch_id,
  });
  const branch = branchData?.data;

  const { data: saleData } = useQuery({
    queryKey: [`/sales/{sale}`, { params: { path: { sale: mCase?.sale_id } } }],
    enabled: !!mCase?.sale_id,
  });
  const sale = saleData?.data;
  const saleItems = sale?.items ?? [];

  if (loading) return <div className="p-6 text-muted-foreground">{t('adm_common.loading')}</div>;
  if (!contract) return <div className="p-6 text-muted-foreground">{t('adm_contractDetail.notFound')}</div>;

  const scheduleItems = contract.payment_schedule ?? [];
  const events = contract.events ?? [];
  const connectorLogs = contract.connector_logs ?? [];

  const paidCount = scheduleItems.filter(s => s.status === 'paid').length;
  const overdueCount = scheduleItems.filter(s => s.status === 'overdue').length;
  const totalPaid = scheduleItems.filter(s => s.status === 'paid').reduce((sum, s) => sum + Number(s.amount), 0);

  // Shartnoma summasi = tovar narxi + ustama; Do'konga to'lanadi = faqat tovar narxi
  const contractTotal = Number(contract.total_amount ?? mCase?.total_amount ?? 0);
  const orders = mCase?.orders ?? [];
  // FR-99: the goods price comes from the order (sale only as a fallback), as Lovable.
  const goodsTotal = Number(orders[0]?.total_amount ?? sale?.total_amount ?? 0);
  const payoutAmount = goodsTotal > 0 ? goodsTotal : contractTotal;
  const markupAmount = Math.max(0, contractTotal - payoutAmount);

  const unifiedContractId = mCase?.contract_number || contract.contract_number || null;
  const consistency = checkMurabahaConsistency(
    mCase ? { ...mCase, contract_id: mCase.contract_number } : null,
    { status: contract.status, contract_number: contract.contract_number },
  );
  const paymentStatus = mCase?.payment_status ?? 'none';
  const limitExpired = customer?.limit_expires_at && new Date(customer.limit_expires_at) < new Date();

  const handlePrint = () => {
    generateContractPDF({
      contract: {
        contract_number: contract.contract_number,
        total_amount: Number(contract.total_amount),
        monthly_payment: Number(contract.monthly_payment),
        term_months: contract.term_months,
        created_at: contract.created_at,
        status: contract.status,
      },
      customer: customer ? {
        full_name: customer.full_name,
        phone: customer.phone,
        passport: customer.passport,
        pinfl: customer.pinfl,
      } : null,
      schedule: (contract.payment_schedule ?? []).map(row => ({ n: row.n, due_date: row.due_date, amount: Number(row.amount), remaining: row.remaining })),
      saleItems: saleItems.map(item => ({
        product_name: item.product_name,
        quantity: Number(item.quantity),
        unit_price: Number(item.unit_price),
        total: Number(item.total),
        unit: item.unit,
        mhik: item.mhik || '',
      })),
    });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/contracts')}>
          <ArrowLeft size={18} />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-foreground font-mono">
            {unifiedContractId || <span className="text-warning font-sans text-base">{t('adm_contractDetail.idAwaitingLm')}</span>}
          </h1>
          <p className="text-sm text-muted-foreground">{merchant?.name || '—'} · {customer?.full_name || '—'}</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {mCase && (
            <Badge className={`text-xs border-0 ${statusColors[mCase.status] || ''}`}>
              {statusLabels()[mCase.status] || mCase.status}
            </Badge>
          )}
          <Badge className={`text-xs border-0 ${paymentStatusColors[paymentStatus] || ''}`}>
            {paymentStatusLabels()[paymentStatus] || paymentStatus}
          </Badge>
          <Badge variant="outline" className={contractStatusColors[contract.status] || ''}>
            {contractStatusLabels()[contract.status] || contract.status}
          </Badge>
          <ConsistencyBadge issues={consistency.issues} />
        </div>
      </div>

      {mCase && <AdminInvoicePanel invoiceId={mCase.invoice_id ?? null} />}

      <BankRegistrationPanel contract={contract} locale={intlLocale()} />

      {/* Nomuvofiqlik paneli */}
      {!consistency.ok && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle size={16} className="text-destructive" />
            <h2 className="text-sm font-semibold text-destructive">
              {t('adm_contractDetail.inconsistent', { count: consistency.issues.length })}
            </h2>
          </div>
          <ul className="space-y-1 text-xs text-foreground">
            {consistency.issues.map(i => (
              <li key={i.code}>• <span className="font-medium">{i.label}:</span> {i.detail}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">{t('adm_contracts.contractAmount')}</CardTitle></CardHeader>
          <CardContent>
            <div className="text-lg font-bold">{formatUZS(contractTotal)}</div>
            <p className="text-[10px] text-muted-foreground mt-0.5">{t('adm_contractDetail.customerPaysHisob')}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">{t('adm_contracts.payoutToStore')}</CardTitle></CardHeader>
          <CardContent>
            <div className="text-lg font-bold text-success">{formatUZS(payoutAmount)}</div>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {t('adm_contractDetail.goodsPriceCap')}{markupAmount > 0 ? ` · ${t('adm_contracts.markup')} ${formatUZS(markupAmount)}` : ''}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">{t('adm_contractDetail.monthlyPayment')}</CardTitle></CardHeader>
          <CardContent>
            <div className="text-lg font-bold">{formatUZS(Number(contract.monthly_payment))}</div>
            <p className="text-xs text-muted-foreground">{t('adm_contracts.monthsCount', { count: contract.term_months })}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">{t('adm_contractDetail.paid')}</CardTitle></CardHeader>
          <CardContent>
            <div className="text-lg font-bold">{formatUZS(totalPaid)}</div>
            <p className="text-xs text-muted-foreground">{t('adm_contractDetail.paymentsCount', { paid: paidCount, total: scheduleItems.length })}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">{t('adm_contractDetail.overdue')}</CardTitle></CardHeader>
          <CardContent><div className={`text-lg font-bold ${overdueCount > 0 ? 'text-destructive' : ''}`}>{overdueCount}</div></CardContent>
        </Card>
      </div>

      {/* Mijoz va ish tafsilotlari */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-xl p-5">
          <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <User size={16} className="text-primary" /> {t('adm_contractDetail.customerInfo')}
          </h2>
          <div className="space-y-1 divide-y divide-border">
            <InfoRow label={t('adm_contractDetail.fullName')} value={customer?.full_name} />
            <InfoRow label={t('adm_merchants.phone')} value={customer?.phone} />
            <InfoRow label={t('adm_merchantCreate.passport')} value={customer?.passport} />
            <InfoRow label="PINFL" value={customer?.pinfl} />
            <InfoRow label="Client ID" value={customer?.client_id ? <span className="font-mono text-xs">{customer.client_id}</span> : (customer?.client_manager_error ? <span className="text-xs text-destructive">{customer.client_manager_error}</span> : '—')} />
            <InfoRow label={t('adm_contracts.status')} value={
              customer?.status === 'active' ? <Badge className="bg-success/10 text-success border-0 text-xs">{t('adm_merchants.st_active')}</Badge> :
              customer?.status === 'blocked' ? <Badge className="bg-destructive/10 text-destructive border-0 text-xs">{t('adm_merchants.st_blocked')}</Badge> :
              <Badge className="bg-muted text-muted-foreground border-0 text-xs">Lead</Badge>
            } />
            <InfoRow label={t('adm_contractDetail.nasiyaLimit')} value={customer?.murabaha_limit ? formatUZS(Number(customer.murabaha_limit)) : '—'} />
            <InfoRow label={t('adm_contractDetail.limitExpiry')} value={
              customer?.limit_expires_at ? (
                <span className={limitExpired ? 'text-destructive' : ''}>
                  {new Date(customer.limit_expires_at).toLocaleDateString(intlLocale())}
                  {limitExpired && ` (${t('adm_contractDetail.expired')})`}
                </span>
              ) : '—'
            } />
            <InfoRow label={t('adm_contractDetail.totalPurchases')} value={`${t('adm_contracts.countPcs', { count: customer?.total_purchases ?? 0 })} / ${formatUZS(Number(customer?.total_spent ?? 0))}`} />
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl p-5">
          <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <FileText size={16} className="text-primary" /> {t('adm_contractDetail.caseDetails')}
          </h2>
          <div className="space-y-1 divide-y divide-border">
            <InfoRow icon={Hash} label={t('adm_contracts.contractId')} value={
              unifiedContractId
                ? <span className="font-mono text-xs">{unifiedContractId}</span>
                : <span className="text-xs text-warning">{t('adm_contractDetail.awaitingLm')}</span>
            } />
            <InfoRow label={t('adm_contracts.store')} value={
              mCase?.merchant_id
                ? <Link to={`/admin/merchants/${mCase.merchant_id}`} className="text-primary hover:underline">{merchant?.name || mCase.merchant_id}</Link>
                : '—'
            } />
            <InfoRow label={t('adm_contractDetail.internalCaseNo')} value={<span className="font-mono text-[10px] text-muted-foreground">{mCase?.id || '—'}</span>} />
            <InfoRow icon={MapPin} label={t('adm_contractDetail.branch')} value={branch?.name || mCase?.branch_id} />
            <InfoRow icon={Calendar} label={t('adm_contractDetail.createdDate')} value={new Date(mCase?.created_at ?? contract.created_at).toLocaleString(intlLocale())} />
            <InfoRow icon={Clock} label={t('adm_contractDetail.lastUpdated')} value={mCase?.updated_at ? new Date(mCase.updated_at).toLocaleString(intlLocale()) : '—'} />
            <InfoRow icon={CreditCard} label={t('adm_contractDetail.paymentStatus')} value={
              <span className="flex flex-col gap-0.5">
                <Badge className={`text-xs border-0 w-fit ${paymentStatusColors[paymentStatus] || ''}`}>
                  {paymentStatusLabels()[paymentStatus] || paymentStatus}
                </Badge>
                <span className="text-[10px] text-muted-foreground">{t('adm_contractDetail.autoFromLm')}</span>
              </span>
            } />
            <InfoRow label={t('adm_contractDetail.payoutAmount')} value={
              <span className="flex flex-col gap-0.5">
                <span className="font-medium text-success">{formatUZS(payoutAmount)}</span>
                <span className="text-[10px] text-muted-foreground">{t('adm_contractDetail.goodsPriceNoMarkup')}</span>
              </span>
            } />
            <InfoRow label={t('adm_contractDetail.limit')} value={Number(mCase?.limit_amount) ? formatUZS(Number(mCase?.limit_amount)) : '—'} />
            <InfoRow label={t('adm_contractDetail.contractStatus')} value={
              <Badge variant="outline" className={contractStatusColors[contract.status] || ''}>
                {contractStatusLabels()[contract.status] || contract.status}
              </Badge>
            } />
            {mCase?.cashier_id && <InfoRow label={t('adm_contractDetail.cashierId')} value={<span className="font-mono text-xs">{mCase.cashier_id}</span>} />}
            {mCase?.sale_id && <InfoRow label={t('adm_contractDetail.saleId')} value={<span className="font-mono text-xs">{mCase.sale_id}</span>} />}
            {mCase?.order_id && <InfoRow label={t('adm_contractDetail.orderId')} value={<span className="font-mono text-xs">{mCase.order_id}</span>} />}
          </div>
        </div>
      </div>

      {/* Sotib olingan tovarlar */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Package size={16} className="text-primary" /> {t('adm_contractDetail.purchasedGoods')}
        </h2>
        {saleItems.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {mCase?.sale_id ? t('adm_contractDetail.noGoodsList') : t('adm_contractDetail.noSaleLinked')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-xs text-muted-foreground p-2 font-medium">{t('adm_contractDetail.product')}</th>
                  <th className="text-left text-xs text-muted-foreground p-2 font-medium">MHIK</th>
                  <th className="text-right text-xs text-muted-foreground p-2 font-medium">{t('adm_contractDetail.qty')}</th>
                  <th className="text-left text-xs text-muted-foreground p-2 font-medium">{t('adm_contractDetail.unit')}</th>
                  <th className="text-right text-xs text-muted-foreground p-2 font-medium">{t('adm_contractDetail.price')}</th>
                  <th className="text-right text-xs text-muted-foreground p-2 font-medium">{t('adm_contractDetail.total')}</th>
                </tr>
              </thead>
              <tbody>
                {saleItems.map((item, idx) => (
                  <tr key={idx} className="border-b border-border last:border-0">
                    <td className="p-2 text-foreground">{item.product_name}</td>
                    <td className="p-2 text-muted-foreground font-mono text-xs">{item.mhik || '—'}</td>
                    <td className="p-2 text-right text-foreground">{item.quantity}</td>
                    <td className="p-2 text-muted-foreground">{item.unit}</td>
                    <td className="p-2 text-right text-foreground">{formatUZS(Number(item.unit_price))}</td>
                    <td className="p-2 text-right font-medium text-foreground">{formatUZS(Number(item.total))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border">
                  <td colSpan={5} className="p-2 text-right text-sm font-medium text-muted-foreground">{t('adm_contractDetail.totalColon')}</td>
                  <td className="p-2 text-right font-semibold text-foreground">{formatUZS(Number(sale?.total_amount ?? payoutAmount))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Shartnoma */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <FileText size={16} className="text-primary" /> {t('adm_contractDetail.contract')}
        </h2>
        <div className="border border-border rounded-lg p-3 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground font-mono">
              {contract.contract_number || <span className="text-warning font-sans">{t('adm_contractDetail.idAwaiting')}</span>}
            </span>
            <Badge variant="outline" className={contractStatusColors[contract.status] || ''}>
              {contractStatusLabels()[contract.status] || contract.status}
            </Badge>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div><span className="text-muted-foreground">{t('adm_contractDetail.amountColon')}</span> <span className="text-foreground">{formatUZS(Number(contract.total_amount))}</span></div>
            <div><span className="text-muted-foreground">{t('adm_contractDetail.monthlyColon')}</span> <span className="text-foreground">{formatUZS(Number(contract.monthly_payment))}</span></div>
            <div><span className="text-muted-foreground">{t('adm_contractDetail.termColon')}</span> <span className="text-foreground">{t('adm_contracts.monthsCount', { count: contract.term_months })}</span></div>
          </div>
          <p className="text-xs text-muted-foreground">{t('adm_contractDetail.createdColon')} {new Date(contract.created_at).toLocaleDateString(intlLocale())}</p>
          <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={handlePrint}>
            <Printer size={14} /> {t('adm_contractDetail.printPdf')}
          </Button>
        </div>
      </div>

      {/* Buyurtmalar */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
          <Package size={16} className="text-primary" /> {t('adm_contractDetail.orders')}
        </h2>
        {orders.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">{t('adm_contractDetail.noOrderData')}</p>
        ) : (
          <div className="space-y-3">
            {orders.map(o => (
              <div key={o.id} className="border border-border rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">#{o.order_number}</span>
                  <Badge className={`text-xs border-0 ${o.status === 'confirmed' ? 'bg-success/10 text-success' : o.status === 'cancelled' ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'}`}>
                    {orderStatusLabels()[o.status] || o.status}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{t('adm_contractDetail.amountColon')} {formatUZS(Number(o.total_amount))} • {new Date(o.created_at).toLocaleDateString(intlLocale())}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="schedule">
        <TabsList>
          <TabsTrigger value="schedule" className="gap-1"><Calendar size={14} /> {t('adm_contractDetail.paymentSchedule')}</TabsTrigger>
          <TabsTrigger value="events" className="gap-1"><Clock size={14} /> {t('adm_contractDetail.events')}</TabsTrigger>
          <TabsTrigger value="logs" className="gap-1"><Activity size={14} /> {t('adm_contractDetail.connectorLogs')}</TabsTrigger>
        </TabsList>

        {/* Payment schedule */}
        <TabsContent value="schedule">
          <Card>
            <CardContent className="pt-6">
              {scheduleItems.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('adm_contractDetail.noSchedule')}</p>
              ) : (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>{t('adm_common.date')}</TableHead>
                        <TableHead>{t('adm_contractDetail.sum')}</TableHead>
                        <TableHead>{t('adm_contracts.status')}</TableHead>
                        <TableHead>{t('adm_contractDetail.paid')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {scheduleItems.map((s, i) => (
                        <TableRow key={i}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell className="font-mono text-xs">{new Date(s.due_date).toLocaleDateString(intlLocale())}</TableCell>
                          <TableCell className="font-mono">{formatUZS(Number(s.amount))}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={scheduleStatusColors[s.status] || ''}>
                              {scheduleStatusLabels()[s.status] || s.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {s.paid_at ? new Date(s.paid_at).toLocaleDateString(intlLocale()) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Events timeline */}
        <TabsContent value="events">
          <Card>
            <CardContent className="pt-6">
              {events.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('adm_contractDetail.noEvents')}</p>
              ) : (
                <div className="space-y-3">
                  {events.map(ev => (
                    <div key={ev.id} className="border-l-2 border-border pl-3 py-1">
                      <p className="text-sm font-medium">{eventTypeLabels()[ev.event_type] || ev.event_type}</p>
                      {ev.payload && Object.keys(ev.payload).length > 0 && (
                        <pre className="text-xs text-muted-foreground mt-0.5 bg-muted rounded p-2 overflow-x-auto max-w-full">
                          {JSON.stringify(ev.payload, null, 2)}
                        </pre>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {new Date(ev.created_at).toLocaleString(intlLocale())}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Connector logs */}
        <TabsContent value="logs">
          <Card>
            <CardContent className="pt-6">
              {connectorLogs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">{t('adm_contractDetail.noLogs')}</p>
              ) : (
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('adm_contractDetail.time')}</TableHead>
                        <TableHead>{t('adm_contractDetail.connector')}</TableHead>
                        <TableHead>{t('adm_contractDetail.direction')}</TableHead>
                        <TableHead>{t('adm_contractDetail.method')}</TableHead>
                        <TableHead>URL</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Body</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {connectorLogs.map(log => (
                        <TableRow key={log.id}>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(log.created_at).toLocaleString(intlLocale())}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{log.connector_code}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={log.direction === 'request' ? 'default' : 'secondary'} className="text-[10px]">
                              {log.direction === 'request' ? `→ ${t('adm_contractDetail.request')}` : `← ${t('adm_contractDetail.response')}`}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">{log.method}</TableCell>
                          <TableCell className="text-xs max-w-[200px] truncate" title={log.url ?? undefined}>{log.url || '—'}</TableCell>
                          <TableCell>
                            <span className="flex items-center gap-1">
                              <StatusIcon status={log.status_code} />
                              <span className="font-mono text-xs">{log.status_code || '—'}</span>
                            </span>
                          </TableCell>
                          <TableCell>
                            <details className="text-xs">
                              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">{t('adm_contractDetail.view')}</summary>
                              <pre className="mt-1 bg-muted rounded p-2 max-w-[300px] overflow-x-auto text-[10px] whitespace-pre-wrap break-all">
                                {log.body ? (log.body.length > 500 ? log.body.slice(0, 500) + '...' : log.body) : '—'}
                              </pre>
                            </details>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
